import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Quick buy page: filter and search a product list, and add or change cart quantities in place.
 */
class QuickBuy extends HTMLElement {
  /** @type {string} */
  #filter = 'all';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-quick-search]')?.addEventListener('input', this.#applyFilters);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.querySelector('[data-quick-search]')?.removeEventListener('input', this.#applyFilters);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (event.target === this) return;
    this.#syncWithCart();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#syncWithCart();
  };

  async #syncWithCart() {
    const { cartUrl } = this.dataset;
    if (!cartUrl) return;

    try {
      const cart = await (await fetch(cartUrl, { cache: 'no-store' })).json();
      for (const control of this.querySelectorAll('[data-variant-id]')) {
        if (control instanceof HTMLElement) {
          this.#setQuantity(control, this.#quantityInCart(cart, Number(control.dataset.variantId)));
        }
      }
      this.#renderSummary(cart);
    } catch {
      // Leave the current state in place; the next update will resync.
    }
  }

  /**
   * @param {{ items: { variant_id: number, quantity: number }[] }} cart
   * @param {number} variantId
   */
  #quantityInCart(cart, variantId) {
    return cart.items.filter((item) => item.variant_id === variantId).reduce((sum, item) => sum + item.quantity, 0);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const chip = event.target.closest('[data-filter]');
    if (chip instanceof HTMLElement) {
      this.#filter = chip.dataset.filter ?? 'all';
      for (const button of this.querySelectorAll('[data-filter]')) {
        button.setAttribute('aria-pressed', String(button === chip));
      }
      this.#applyFilters();
      return;
    }

    const toggle = event.target.closest('[data-variants-toggle]');
    if (toggle instanceof HTMLElement) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls') ?? '');
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
      return;
    }

    const button = event.target.closest('[data-qty-change]');
    if (!(button instanceof HTMLElement)) return;

    const control = button.closest('[data-variant-id]');
    if (!(control instanceof HTMLElement)) return;

    const current = Number(control.dataset.quantity ?? 0);
    this.#updateQuantity(control, Math.max(0, current + Number(button.dataset.qtyChange)));
  };

  #applyFilters = () => {
    const search = this.querySelector('[data-quick-search]');
    const query = search instanceof HTMLInputElement ? search.value.trim().toLowerCase() : '';
    let visible = 0;

    for (const row of this.querySelectorAll('.quick-buy__item')) {
      if (!(row instanceof HTMLElement)) continue;
      const matchesFilter = this.#filter === 'all' || (row.dataset.collections ?? '').includes(` ${this.#filter} `);
      const matchesQuery = !query || (row.dataset.title ?? '').includes(query);
      row.hidden = !(matchesFilter && matchesQuery);
      if (!row.hidden) visible += 1;
    }

    const empty = this.querySelector('[data-quick-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visible > 0;
  };

  /**
   * @param {HTMLElement} control
   * @param {number} quantity
   */
  async #updateQuantity(control, quantity) {
    const variantId = Number(control.dataset.variantId);
    const current = Number(control.dataset.quantity ?? 0);
    const { cartAddUrl, cartChangeUrl, cartUrl } = this.dataset;
    if (!cartAddUrl || !cartChangeUrl || !cartUrl) return;

    control.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      const adding = current === 0;
      const response = await fetch(adding ? cartAddUrl : cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          adding ? { items: [{ id: variantId, quantity }] } : { id: String(variantId), quantity }
        ),
      });
      if (!response.ok) throw new Error(String(response.status));

      const cart = adding ? await (await fetch(cartUrl)).json() : await response.json();
      this.#setQuantity(control, this.#quantityInCart(cart, variantId));
      this.#renderSummary(cart);
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id || 'quick-buy', { itemCount: cart.item_count, source: 'quick-buy' })
      );
    } catch {
      this.#showError(this.dataset.error ?? '');
    } finally {
      control.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement} control
   * @param {number} quantity
   */
  #setQuantity(control, quantity) {
    control.dataset.quantity = String(quantity);
    const label = control.querySelector('[data-qty]');
    if (label) label.textContent = String(quantity);
  }

  /** @param {{ total_price: number, item_count: number }} cart */
  #renderSummary(cart) {
    const format = this.dataset.moneyFormat ?? '';
    const currency = this.dataset.currency ?? '';
    const money = (/** @type {number} */ cents) => formatMoney(cents, format, currency);

    const subtotal = this.querySelector('[data-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);

    const count = this.querySelector('[data-count]');
    if (count) {
      count.textContent =
        cart.item_count === 1
          ? this.dataset.itemsOne ?? ''
          : (this.dataset.itemsOther ?? '').replace('[count]', String(cart.item_count));
    }

    const threshold = Number(this.dataset.threshold ?? 0);
    if (threshold <= 0) return;

    const progress = this.querySelector('[data-progress]');
    if (progress instanceof HTMLElement) {
      progress.style.width = `${Math.min(100, Math.floor((cart.total_price * 100) / threshold))}%`;
    }

    const shipping = this.querySelector('[data-shipping]');
    if (shipping) {
      const remaining = threshold - cart.total_price;
      shipping.textContent =
        remaining > 0
          ? (this.dataset.shippingRemaining ?? '').replace('[amount]', money(remaining))
          : this.dataset.shippingUnlocked ?? '';
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-error-message]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('quick-buy')) {
  customElements.define('quick-buy', QuickBuy);
}
